import { useState } from 'react';
import { Send, Mail, Instagram, MapPin, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import PageWrapper from '@/components/PageWrapper';
import { FadeUp, SlideLeft, SlideRight } from '@/components/ScrollReveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      {/* Hero */}
      <section data-testid="contact-hero" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <div className="text-center max-w-2xl mx-auto">
              <Mail className="mx-auto mb-4 text-stone-400" size={32} strokeWidth={1} />
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">Reach Out</p>
              <h1 className="font-heading text-5xl sm:text-6xl text-stone-900 leading-[1.1] tracking-tight">
                Get in <span className="italic">Touch</span>
              </h1>
              <p className="mt-8 text-stone-600 text-lg leading-relaxed font-light">
                Have questions or ready to start your TEZR journey? I'd love to hear from you.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Contact Form */}
      <section data-testid="contact-form-section" className="py-24 md:py-32 bg-stone-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-5 gap-16">
            {/* Info */}
            <div className="lg:col-span-2">
              <SlideLeft>
                <h2 className="font-heading text-3xl text-stone-900 mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="p-6 bg-white border border-stone-200">
                    <div className="flex items-start gap-4">
                      <MapPin size={18} strokeWidth={1.5} className="text-stone-500 mt-1" />
                      <div>
                        <h4 className="font-medium text-stone-900 mb-1">Location</h4>
                        <p className="text-stone-500 text-sm font-light">
                          Kingsland, Auckland<br />New Zealand
                        </p>
                      </div>
                    </div>
                  </div>

                  <a
                    href="https://www.instagram.com/tezrpilates"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram"
                    className="block p-6 bg-white border border-stone-200 hover:border-stone-400 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <Instagram size={18} strokeWidth={1.5} className="text-stone-500 mt-1" />
                      <div>
                        <h4 className="font-medium text-stone-900 mb-1">Instagram</h4>
                        <p className="text-stone-500 text-sm font-light">@tezrpilates</p>
                      </div>
                    </div>
                  </a>
                </div>

                <div className="mt-8 image-zoom aspect-video">
                  <img
                    src="/images/THE STUDIO.jpg"
                    alt="TEZR Studio"
                    className="w-full h-full object-cover editorial-image"
                  />
                </div>
              </SlideLeft>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <SlideRight>
                <div className="bg-white border border-stone-200 p-8">
                  <h3 className="font-heading text-2xl text-stone-900 mb-6">Send a Message</h3>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-stone-700 text-sm">
                          Name <span className="text-stone-400">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          data-testid="contact-name-input"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your name"
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-stone-700 text-sm">
                          Email <span className="text-stone-400">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          data-testid="contact-email-input"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className="form-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-stone-700 text-sm">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          data-testid="contact-phone-input"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Your phone"
                          className="form-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject" className="text-stone-700 text-sm">Subject</Label>
                        <Input
                          id="subject"
                          name="subject"
                          data-testid="contact-subject-input"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="What's this about?"
                          className="form-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-stone-700 text-sm">
                        Message <span className="text-stone-400">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        data-testid="contact-message-input"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell me about your goals..."
                        rows={6}
                        className="form-textarea"
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      data-testid="contact-submit-btn"
                      disabled={isSubmitting}
                      className="w-full btn-primary py-4"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" size={16} />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2" size={16} strokeWidth={1.5} />
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              </SlideRight>
            </div>
          </div>
        </div>
      </section>
    </PageWrapper>
  );
};

export default ContactPage;
